import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../api/client.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { formatDistanceToNow } from 'date-fns';

/**
 * Admin / Overview — the platform-operator business dashboard.
 *
 *   1. KPI tiles: MRR, ARR, paying customers, trials, conversion,
 *      AI cost vs MRR (gross margin), churn risk.
 *   2. Cash collected by period (this/last month, YTD, LYTD, last year).
 *   3. Subscriptions + trials + income broken down by plan tier.
 *   4. Customer economics table: revenue vs AI cost per org, posts,
 *      activity, quota pressure.
 *
 * All money from the API is integer cents; ai costs are USD floats.
 * Server gate is requirePlatformAdmin — the redirect below is UX only.
 */

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

function money(cents, { compactZero = false } = {}) {
  const n = (Number(cents) || 0) / 100;
  if (n === 0 && compactZero) return '—';
  return Number.isInteger(n) ? usd0.format(n) : usd2.format(n);
}

function aiCost(usd) {
  const n = Number(usd) || 0;
  if (n === 0) return '—';
  return n < 1 ? `$${n.toFixed(3)}` : usd2.format(n);
}

function pct(x) {
  return x == null ? '—' : `${(x * 100).toFixed(0)}%`;
}

function ago(iso) {
  return iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : '—';
}

function shortDate(iso) {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

export default function AdminOverview() {
  const { user } = useAuth();
  const isOperator = !!user?.is_platform_admin;

  // Hooks run unconditionally (rules of hooks) — `enabled` keeps
  // non-operators from ever firing the requests; the guard below redirects.
  const overview = useQuery({
    queryKey: ['admin-overview'],
    queryFn: ({ signal }) => apiGet('/admin/overview', { signal }),
    refetchInterval: 60_000,
    enabled: isOperator,
  });
  const customers = useQuery({
    queryKey: ['admin-customers'],
    queryFn: ({ signal }) => apiGet('/admin/customers', { signal }),
    refetchInterval: 60_000,
    enabled: isOperator,
  });

  const queryClient = useQueryClient();
  const backfill = useMutation({
    mutationFn: () => apiPost('/admin/billing/backfill-invoices'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
    },
  });

  if (!isOperator) return <Navigate to="/dashboard" replace />;
  if (overview.isLoading) return <div className="loading">Loading admin overview…</div>;
  if (overview.isError) return <div className="error">Couldn't load: {overview.error.message}</div>;
  const o = overview.data;
  if (!o) return null;

  const k = o.kpis;
  // Gross margin on recurring revenue: AI spend on external (customer) orgs
  // is the COGS of this product. Null until there's MRR to divide by.
  const grossMargin = k.mrr_cents > 0
    ? (k.mrr_cents / 100 - k.ai_cost_this_month_external_usd) / (k.mrr_cents / 100)
    : null;

  return (
    <div className="admin-overview-page">
      <div className="admin-head">
        <div>
          <h2>Platform overview</h2>
          <p className="meta">
            Business KPIs across all customer orgs (internal excluded from revenue).
            Auto-refreshes every 60s. <Link to="/admin/quotas">Quotas dashboard →</Link>
          </p>
        </div>
        <button
          className="btn btn-compact"
          onClick={() => backfill.mutate()}
          disabled={backfill.isPending}
          title="Re-sync paid invoices from Stripe into the local ledger (idempotent)"
        >
          {backfill.isPending ? 'Syncing…' : 'Sync Stripe invoices'}
        </button>
      </div>
      {backfill.isSuccess && (
        <p className="meta">
          Synced {backfill.data.recorded} invoice{backfill.data.recorded === 1 ? '' : 's'}
          {backfill.data.refunds ? ` and ${backfill.data.refunds.applied} refund update${backfill.data.refunds.applied === 1 ? '' : 's'}` : ''} from Stripe.
          {(backfill.data.truncated || backfill.data.refunds?.truncated) && (
            <span className="admin-flag danger"> TRUNCATED — run again to continue.</span>
          )}
          {backfill.data.refunds?.unmatched > 0 && (
            <span className="admin-flag danger"> {backfill.data.refunds.unmatched} refund(s) had no ledger row — check logs.</span>
          )}
        </p>
      )}
      {backfill.isError && <div className="error">Backfill failed: {backfill.error.message}</div>}

      {/* ─── Headline KPIs ─────────────────────────────────────────────── */}
      <div className="admin-kpi-grid">
        <Kpi label="MRR" value={money(k.mrr_cents)} sub={`ARR ${money(k.arr_cents)}`} />
        <Kpi label="Paying customers" value={k.paying_orgs} sub={k.past_due > 0 ? `${k.past_due} past due` : 'all current'} tone={k.past_due > 0 ? 'danger' : ''} />
        <Kpi
          label="Active trials"
          value={k.trial_orgs}
          sub={`${k.trials_ending_7d} ending ≤7d · ${k.trials_expired} expired`}
          tone={k.trials_ending_7d > 0 ? 'warn' : ''}
        />
        <Kpi label="Trial → paid" value={pct(k.conversion_rate)} sub="ever paid vs expired" />
        <Kpi
          label="Pending MRR"
          value={money(k.pending_mrr_cents)}
          sub="trials with card on file"
        />
        <Kpi
          label="AI cost (month)"
          value={aiCost(k.ai_cost_this_month_usd)}
          sub={`customers ${aiCost(k.ai_cost_this_month_external_usd)} · last mo ${aiCost(k.ai_cost_last_month_usd)}`}
        />
        <Kpi
          label="Gross margin"
          value={pct(grossMargin)}
          sub="(MRR − customer AI cost) / MRR"
          tone={grossMargin != null && grossMargin < 0.7 ? 'warn' : ''}
        />
        <Kpi
          label="Churn risk"
          value={k.scheduled_cancels + k.past_due + k.open_disputes}
          sub={`${k.scheduled_cancels} cancel scheduled · ${k.past_due} past due · ${k.open_disputes} disputes`}
          tone={k.scheduled_cancels + k.past_due + k.open_disputes > 0 ? 'danger' : ''}
        />
      </div>

      {/* ─── Income by period ──────────────────────────────────────────── */}
      <section className="section">
        <h3>Cash collected (net of refunds)</h3>
        <div className="admin-kpi-grid admin-kpi-grid-tight">
          <Kpi label="This month" value={money(o.income_periods.this_month_cents)} sub={`${o.new_subscriptions.this_month} new subs`} />
          <Kpi label="Last month" value={money(o.income_periods.last_month_cents)} sub={`${o.new_subscriptions.last_month} new subs`} />
          <Kpi label="This year" value={money(o.income_periods.this_year_cents)} sub={`${o.new_subscriptions.this_year} new subs`} />
          <Kpi label="Last year to date" value={money(o.income_periods.last_year_to_date_cents)} sub={`${o.new_subscriptions.last_year_to_date} new subs`} />
          <Kpi label="Last year (full)" value={money(o.income_periods.last_year_cents)} sub={`${o.new_subscriptions.last_year} new subs`} />
          <Kpi label="All time" value={money(o.income_periods.all_time_cents)} sub="since launch" />
        </div>
      </section>

      {/* ─── By tier ───────────────────────────────────────────────────── */}
      <section className="section admin-tier-row">
        <div>
          <h3>Subscriptions by plan</h3>
          <table className="admin-quotas-table">
            <thead>
              <tr><th>Plan</th><th>Monthly</th><th>Annual</th><th>Total</th><th>MRR</th></tr>
            </thead>
            <tbody>
              {o.subs_by_tier.length === 0 ? (
                <tr><td colSpan={5} className="meta">No paying subscriptions yet.</td></tr>
              ) : o.subs_by_tier.map((t) => (
                <tr key={t.tier}>
                  <td><span className={`plan-pill plan-${t.tier}`}>{t.tier}</span>{t.past_due > 0 && <span className="admin-flag danger"> {t.past_due} past due</span>}</td>
                  <td className="num">{t.monthly}</td>
                  <td className="num">{t.annual}</td>
                  <td className="num">{t.total}</td>
                  <td className="num">{money(t.mrr_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3>Trials by plan</h3>
          <table className="admin-quotas-table">
            <thead>
              <tr><th>Plan</th><th>Card on file</th><th>No card</th><th>Ending ≤7d</th><th>Expired</th></tr>
            </thead>
            <tbody>
              {o.trials_by_tier.length === 0 ? (
                <tr><td colSpan={5} className="meta">No active trials.</td></tr>
              ) : o.trials_by_tier.map((t) => (
                <tr key={t.tier}>
                  <td><span className={`plan-pill plan-${t.tier}`}>{t.tier}</span></td>
                  <td className="num">{t.card_on_file}</td>
                  <td className="num">{t.no_card}</td>
                  <td className="num">{t.ending_7d}</td>
                  <td className="num">{t.expired}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3>Income by plan</h3>
          <table className="admin-quotas-table">
            <thead>
              <tr><th>Plan</th><th>This month</th><th>Last month</th><th>This year</th></tr>
            </thead>
            <tbody>
              {o.income_by_tier.length === 0 ? (
                <tr><td colSpan={4} className="meta">No income recorded yet.</td></tr>
              ) : o.income_by_tier.map((t) => (
                <tr key={t.tier}>
                  <td><span className={`plan-pill plan-${t.tier}`}>{t.tier}</span></td>
                  <td className="num">{money(t.this_month_cents)}</td>
                  <td className="num">{money(t.last_month_cents)}</td>
                  <td className="num">{money(t.this_year_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─── Customer economics ────────────────────────────────────────── */}
      <section className="section">
        <h3>Customers</h3>
        <p className="meta">
          Revenue vs AI cost per organization — the per-customer margin view.
          AI % = this month's AI cost / MRR (lower is better; blank until paying).
        </p>
        {customers.isLoading && <div className="loading">Loading customers…</div>}
        {customers.isError && <div className="error">Couldn't load: {customers.error.message}</div>}
        {customers.data && (
          <div className="admin-table-scroll">
            <table className="admin-quotas-table admin-customers-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>MRR</th>
                  <th>Income (mo)</th>
                  <th>Income (total)</th>
                  <th>AI cost (mo)</th>
                  <th>AI %</th>
                  <th>Posts (mo)</th>
                  <th>Pub 30d</th>
                  <th>Projects</th>
                  <th>Social</th>
                  <th>Quota</th>
                  <th>Last active</th>
                  <th>Renews / ends</th>
                  <th>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {customers.data.customers.map((c) => <CustomerRow key={c.id} c={c} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, sub, tone = '' }) {
  return (
    <div className={`summary-tile admin-kpi ${tone}`}>
      <div className="summary-tile-label">{label}</div>
      <div className="summary-tile-value">{value}</div>
      {sub && <div className="admin-kpi-sub">{sub}</div>}
    </div>
  );
}

function CustomerRow({ c }) {
  // AI-vs-revenue ratio: this month's AI cost against MRR. Trials burn AI
  // with no revenue — flagged, not red, because that's the trial's job.
  const mrrUsd = c.mrr_cents / 100;
  const ratio = mrrUsd > 0 ? c.ai_cost_month_usd / mrrUsd : null;
  const ratioClass = ratio == null ? '' : ratio < 0.2 ? 'ok' : ratio < 0.6 ? 'warn' : 'danger';

  const postsPct = c.posts_per_month > 0 ? c.posts_used_this_month / c.posts_per_month : 0;
  const aiPct = c.ai_calls_per_day > 0 ? c.ai_calls_used_today / c.ai_calls_per_day : 0;
  const quotaPressure = Math.max(postsPct, aiPct);
  const quotaClass = quotaPressure >= 1 ? 'danger' : quotaPressure >= 0.8 ? 'warn' : '';

  const lastActive = [c.last_login_at, c.last_post_at]
    .filter(Boolean)
    .sort()
    .pop() || null;

  const statusLabel = c.billing_status || c.org_status || '—';
  const endDate = c.billing_status === 'trialing' ? c.trial_ends_at : c.current_period_end;

  return (
    <tr className={c.is_internal ? 'admin-row-internal' : ''}>
      <td>
        <strong>{c.name}</strong>
        {c.is_internal && <span className="admin-flag queued"> internal</span>}
        <br /><small>{c.slug} · {c.members_count} member{c.members_count === 1 ? '' : 's'}</small>
      </td>
      <td>
        <span className={`plan-pill plan-${c.plan_tier || 'trial'}`}>{c.plan_tier || 'trial'}</span>
        {c.billing_cadence === 'annual' && <small> /yr</small>}
      </td>
      <td>
        <span className={`admin-status admin-status-${statusLabel}`}>{statusLabel}</span>
        {c.cancel_at_period_end && <span className="admin-flag danger"> canceling</span>}
        {c.billing_status === 'trialing' && c.has_subscription && <span className="admin-flag ok"> card ✓</span>}
      </td>
      <td className="num">{c.mrr_cents > 0 ? money(c.mrr_cents) : c.pending_mrr_cents > 0 ? <span className="meta">({money(c.pending_mrr_cents)})</span> : '—'}</td>
      <td className="num">{money(c.income_month_cents, { compactZero: true })}</td>
      <td className="num">{money(c.income_total_cents, { compactZero: true })}</td>
      <td className="num">{aiCost(c.ai_cost_month_usd)}</td>
      <td className={`num admin-ratio ${ratioClass}`}>{ratio == null ? '—' : `${(ratio * 100).toFixed(0)}%`}</td>
      <td className="num">{c.posts_this_month}<small> / {c.posts_total} total</small></td>
      <td className="num">{c.published_30d}</td>
      <td className="num">{c.projects_count}</td>
      <td className="num">{c.social_active}{c.social_total > c.social_active && <small> / {c.social_total}</small>}</td>
      <td className={`num ${quotaClass}`}>{Math.round(quotaPressure * 100)}%</td>
      <td title={lastActive || ''}>{ago(lastActive)}</td>
      <td title={endDate || ''}>{shortDate(endDate)}</td>
      <td title={c.created_at}>{shortDate(c.created_at)}</td>
    </tr>
  );
}
